import { useAnalytics } from '../hooks/useAnalytics';
import { Spinner } from '../components/ui';

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '14px 16px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || 'var(--text)', letterSpacing: '-0.5px', lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function WeeklyChart({ data }) {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 90, paddingTop: 16 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          {d.count > 0 && (
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)' }}>{d.count}</div>
          )}
          <div style={{
            width: '100%', borderRadius: 4,
            background: d.count > 0 ? 'var(--accent)' : 'var(--border)',
            height: `${Math.max((d.count / max) * 64, d.count > 0 ? 6 : 3)}px`,
            transition: 'height 0.5s ease',
            opacity: d.count > 0 ? 1 : 0.35,
          }} />
          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

function CategoryBar({ cat }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.category}</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500, flexShrink: 0, marginLeft: 8 }}>
          {cat.done}/{cat.total}
        </span>
      </div>
      <div style={{ background: 'var(--border)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 4, background: cat.color,
          width: `${cat.pct}%`, transition: 'width 0.7s ease',
        }} />
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const { summary, streak, weekly, byCategory, loading } = useAnalytics();

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: 'clamp(16px,4vw,24px) clamp(12px,4vw,16px) 80px' }}>

      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Analytics</h1>
        <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>Your productivity at a glance.</p>
      </div>

      {/* Streak banner */}
      {(streak?.streak ?? 0) > 0 && (
        <div style={{
          background: 'var(--accent)', borderRadius: 10,
          padding: '14px 18px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.65)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Current streak
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'white' }}>
              🔥 {streak.streak} day{streak.streak !== 1 ? 's' : ''}
            </div>
          </div>
          {streak?.streak_message && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', maxWidth: 140, textAlign: 'right', lineHeight: 1.4 }}>
              {streak.streak_message}
            </div>
          )}
        </div>
      )}

      {/* Stats grid — 2 cols on mobile, 3 on wider */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 10, marginBottom: 16,
      }}>
        <StatCard label="Total tasks"     value={summary?.total          ?? 0} />
        <StatCard label="Completed"       value={summary?.done           ?? 0} color="var(--accent)" />
        <StatCard label="Done today"      value={summary?.today_done     ?? 0} />
        <StatCard label="Completion rate" value={`${summary?.completion_rate ?? 0}%`} color="var(--accent)" />
        <StatCard label="Overdue"         value={summary?.overdue ?? 0} color={summary?.overdue > 0 ? '#DC2626' : undefined} />
        <StatCard label="Streak"          value={`${streak?.streak ?? 0}d`} />
      </div>

      {/* Weekly chart */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 10, padding: '16px', marginBottom: 12,
      }}>
        <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>Last 7 days</h2>
        <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>Tasks completed per day</p>
        {weekly.length > 0
          ? <WeeklyChart data={weekly} />
          : <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text3)', fontSize: 13 }}>
              No data yet — start completing tasks!
            </div>
        }
      </div>

      {/* By category */}
      {byCategory.length > 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '16px',
        }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 14 }}>By category</h2>
          {byCategory.map(c => <CategoryBar key={c.category} cat={c} />)}
        </div>
      )}
    </div>
  );
}